import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings';
import { useEmailConfirmation } from '../hooks/useEmailConfirmation';
import { useNotificationPreferences } from '../hooks/useNotificationPreferences';
import { useWalletOfferControls } from '../hooks/useWalletOfferControls';
import { useRewardAddresses } from '../hooks/useRewardAddresses';
import { useBlockedWallets } from '../hooks/useBlockedWallets';
import { useNotificationStore } from '../store/notificationStore';
import { getDiscordLinkUrl, unlinkDiscord } from '../api/settings';
import { Badge } from '../ui/components/Badge';
import { Button } from '../ui/components/Button';
import { TextArea } from '../ui/components/TextArea';
import { TextField } from '../ui/components/TextField';
import { Switch } from '../ui/components/Switch';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { FeatherAlertCircle, FeatherGlobe, FeatherTwitter, FeatherUpload, FeatherCheckCircle, FeatherBell, FeatherGift, FeatherBan } from '@subframe/core';
import { getAmbassadorStatus } from '../api/ambassadors';

export const SettingsPage: React.FC = () => {
  const {
    settings,
    isLoading,
    isUpdating,
    isUploadingAvatar,
    error,
    updateError,
    uploadError,
    updateSettings,
    uploadAvatar,
    isUpdateSuccess,
    refetch: refetchSettings,
  } = useSettings();

  const {
    setEmail,
    removeEmail,
    resendConfirmation,
    isSettingEmail,
    setEmailError,
    setEmailSuccess,
    setEmailData,
    isRemovingEmail,
    removeEmailSuccess,
    isResending,
    resendError,
    resendSuccess,
  } = useEmailConfirmation();

  const {
    preferences: notificationPreferences,
    isLoading: isLoadingPreferences,
    isUpdating: isUpdatingPreferences,
    isDirty: isNotificationsDirty,
    updateError: notificationUpdateError,
    togglePreference,
    savePreferences: saveNotificationPreferences,
    resetPreferences: resetNotificationPreferences,
    isUpdateSuccess: isNotificationUpdateSuccess,
  } = useNotificationPreferences();

  const {
    controls: offerControls,
    isLoading: isLoadingOfferControls,
    isUpdating: isUpdatingOfferControls,
    isDirty: isOfferControlsDirty,
    updateError: offerControlsUpdateError,
    updateControl: updateOfferControl,
    saveControls: saveOfferControls,
    resetControls: resetOfferControls,
    isUpdateSuccess: isOfferControlsUpdateSuccess,
  } = useWalletOfferControls();

  const {
    addresses: rewardAddresses,
    isLoading: isLoadingRewardAddresses,
    isDirty: isRewardAddressesDirty,
    isUpdating: isUpdatingRewardAddresses,
    updateError: rewardAddressesUpdateError,
    updateAddress: updateRewardAddress,
    saveAddresses: saveRewardAddresses,
    resetAddresses: resetRewardAddresses,
    isUpdateSuccess: isRewardAddressesUpdateSuccess,
    validateCoreum,
  } = useRewardAddresses();

  const {
    blocked: blockedWallets,
    isLoading: isLoadingBlockedWallets,
    block: blockAddress,
    unblock: unblockAddress,
    isBlocking,
    isUnblocking,
    blockError,
  } = useBlockedWallets();

  const { success: showSuccessToast, error: showErrorToast } = useNotificationStore();
  const [searchParams, setSearchParams] = useSearchParams();

  // Ambassador status (for locking X account field)
  const [isActiveAmbassador, setIsActiveAmbassador] = useState(false);

  // Discord linking state
  const [isUnlinkingDiscord, setIsUnlinkingDiscord] = useState(false);

  useEffect(() => {
    getAmbassadorStatus()
      .then((res) => {
        if (res.data?.enrolled) {
          setIsActiveAmbassador(true);
        }
      })
      .catch(() => {
        // Not an ambassador or not logged in — ignore
      });
  }, []);

  // Handle Discord OAuth callback query params
  useEffect(() => {
    const discordResult = searchParams.get('discord');
    if (!discordResult) return;

    if (discordResult === 'linked') {
      showSuccessToast('Discord account linked successfully!', { title: 'Discord Connected' });
    } else if (discordResult === 'error') {
      const reason = searchParams.get('reason');
      const messages: Record<string, string> = {
        not_in_server: 'You must join the xrp.deals Discord server before linking your account.',
        already_linked: 'This Discord account is already linked to another user.',
        not_authenticated: 'You must be logged in to link your Discord account.',
        not_configured: 'Discord integration is not configured. Please try again later.',
        denied: 'Discord authorization was cancelled.',
        exchange_failed: 'Failed to connect to Discord. Please try again.',
        invalid_state: 'Your session expired. Please try again.',
        expired_state: 'Your session expired. Please try again.',
      };
      showErrorToast(messages[reason || ''] || 'Something went wrong linking your Discord account. Please try again.', { title: 'Discord Link Failed' });
    }

    // Clear query params from URL
    searchParams.delete('discord');
    searchParams.delete('reason');
    setSearchParams(searchParams, { replace: true });
  }, []);

  // Blocked wallets state
  const [blockInput, setBlockInput] = useState('');
  const [confirmUnblock, setConfirmUnblock] = useState<string | null>(null);

  // Form state
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [xAccount, setXAccount] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmailInput] = useState('');
  const [formDirty, setFormDirty] = useState(false);

  // Avatar upload
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize form when settings load
  useEffect(() => {
    if (settings && !formDirty) {
      setUsername(settings.username || '');
      setBio(settings.bio || '');
      setXAccount(settings.social_links?.x_account || '');
      setWebsite(settings.social_links?.website || '');
      setEmailInput(settings.email || '');
    }
  }, [settings, formDirty]);

  // Show toast when settings are saved successfully
  useEffect(() => {
    if (isUpdateSuccess || isNotificationUpdateSuccess || isOfferControlsUpdateSuccess || isRewardAddressesUpdateSuccess) {
      showSuccessToast('Your settings have been saved', { title: 'Settings Saved' });
    }
  }, [isUpdateSuccess, isNotificationUpdateSuccess, isOfferControlsUpdateSuccess, isRewardAddressesUpdateSuccess, showSuccessToast]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Save profile settings if changed
    if (formDirty) {
      updateSettings({
        username,
        bio: bio || null,
        social_links: {
          x_account: xAccount || null,
          website: website || null,
        },
      });
      setFormDirty(false);
    }
    // Save notification preferences if changed
    if (isNotificationsDirty) {
      saveNotificationPreferences();
    }
    // Save offer controls if changed
    if (isOfferControlsDirty) {
      saveOfferControls();
    }
    // Save reward addresses if changed
    if (isRewardAddressesDirty) {
      saveRewardAddresses();
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('File must be JPEG, PNG, GIF, or WebP');
        return;
      }

      uploadAvatar(file);
    }
  };

  const handleSetEmail = () => {
    if (email && email !== settings?.email) {
      setEmail(email);
    }
  };

  const handleRemoveEmail = () => {
    if (window.confirm('Are you sure you want to remove your email address?')) {
      removeEmail();
    }
  };

  const handleResendConfirmation = () => {
    resendConfirmation();
  };

  const handleCancel = () => {
    if (settings) {
      setUsername(settings.username || '');
      setBio(settings.bio || '');
      setXAccount(settings.social_links?.x_account || '');
      setWebsite(settings.social_links?.website || '');
      setEmailInput(settings.email || '');
      setFormDirty(false);
    }
    // Reset notification preferences
    resetNotificationPreferences();
    // Reset offer controls
    resetOfferControls();
    // Reset reward addresses
    resetRewardAddresses();
  };

  const handleUnlinkDiscord = async () => {
    if (!window.confirm('Are you sure you want to unlink your Discord account?')) return;
    setIsUnlinkingDiscord(true);
    try {
      await unlinkDiscord();
      showSuccessToast('Discord account unlinked', { title: 'Discord Disconnected' });
      refetchSettings();
    } catch (err: any) {
      showErrorToast(err.message || 'Failed to unlink Discord account');
    } finally {
      setIsUnlinkingDiscord(false);
    }
  };

  if (isLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full flex-col items-start">
          <div className="container max-w-none flex w-full grow shrink-0 basis-0 flex-col items-center gap-4 bg-default-background py-12">
            <div className="text-center py-12">Loading settings...</div>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  if (error) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full flex-col items-start">
          <div className="container max-w-none flex w-full grow shrink-0 basis-0 flex-col items-center gap-4 bg-default-background py-12">
            <div className="flex w-full max-w-[768px] flex-col items-start gap-4">
              <div className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                Failed to load settings: {error.message}
              </div>
            </div>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-8 pb-12">
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-heading-1 font-heading-1 text-default-font">
                Settings
              </span>
              <span className="text-body font-body text-subtext-color">
                Manage your account settings and preferences
              </span>
            </div>

            {/* Success Messages */}
            {setEmailSuccess && setEmailData && (
              <div className="w-full bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                {setEmailData.message}
              </div>
            )}

            {removeEmailSuccess && (
              <div className="w-full bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                Email removed successfully
              </div>
            )}

            {resendSuccess && (
              <div className="w-full bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                Confirmation email sent!
              </div>
            )}

            {/* Error Messages */}
            {updateError && (
              <div className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                Failed to update settings: {updateError.message}
              </div>
            )}

            {uploadError && (
              <div className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                Failed to upload avatar: {uploadError.message}
              </div>
            )}

            {setEmailError && (
              <div className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                Failed to set email: {setEmailError.message}
              </div>
            )}

            {resendError && (
              <div className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {resendError.message.includes('429')
                  ? 'Too many requests. You can resend confirmation emails up to 3 times per hour.'
                  : `Failed to resend confirmation: ${resendError.message}`}
              </div>
            )}

            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

            <form onSubmit={handleSubmit} className="flex w-full flex-col items-start gap-8">
              {/* Profile Section */}
              <div className="flex w-full flex-col items-start gap-6">
                <span className="w-full text-caption font-caption text-subtext-color">
                  PROFILE
                </span>
                <div className="flex w-full items-start gap-6">
                  <div className="flex h-24 w-24 flex-none flex-col items-center justify-center gap-2 overflow-hidden rounded-full bg-brand-100 relative">
                    {settings?.avatar_url ? (
                      <img
                        className="h-24 w-24 flex-none object-cover absolute"
                        src={settings.avatar_url}
                        alt="Avatar"
                      />
                    ) : (
                      <div className="h-24 w-24 flex items-center justify-center text-2xl text-brand-600">
                        {settings?.username?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleAvatarChange}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                    />
                    <Button
                      variant="neutral-secondary"
                      icon={<FeatherUpload />}
                      disabled={isUploadingAvatar}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        event.preventDefault();
                        fileInputRef.current?.click();
                      }}
                    >
                      {isUploadingAvatar ? 'Uploading...' : 'Upload new avatar'}
                    </Button>
                    <span className="text-caption font-caption text-subtext-color">
                      Recommended: Square image, at least 512x512px (Max 5MB)
                    </span>
                  </div>
                </div>
                <div className="flex w-full flex-col items-start gap-2">
                  <span className="text-body-bold font-body-bold text-default-font">
                    XRPL Address
                  </span>
                  <div className="flex w-full items-center gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
                    <span className="grow shrink-0 basis-0 text-body font-body text-default-font">
                      {settings?.xrpl_address || 'Loading...'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

              {/* Account Information Section */}
              <div className="flex w-full flex-col items-start gap-6">
                <span className="w-full text-caption font-caption text-subtext-color">
                  ACCOUNT INFORMATION
                </span>
                <TextField
                  className="h-auto w-full flex-none"
                  label="Username"
                  helpText={
                    username.length > 0 && (username.length < 4 || username.length > 18)
                      ? 'Username must be between 4-18 characters'
                      : '4-18 characters, alphanumeric and underscores only'
                  }
                  error={username.length > 0 && (username.length < 4 || username.length > 18)}
                >
                  <TextField.Input
                    placeholder="your_username"
                    value={username}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setUsername(event.target.value);
                      setFormDirty(true);
                    }}
                  />
                </TextField>

                {/* Email Field and Status */}
                <div className="flex w-full flex-col items-start gap-4">
                  <TextField
                    className="h-auto w-full flex-none"
                    label="Email address"
                    helpText=""
                  >
                    <TextField.Input
                      placeholder="your@email.com"
                      type="email"
                      value={email}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setEmailInput(event.target.value);
                      }}
                    />
                  </TextField>

                  {settings?.email ? (
                    <div className="flex w-full items-center gap-2">
                      {settings.email_confirmed ? (
                        <Badge variant="success" icon={<FeatherCheckCircle />}>
                          Verified
                        </Badge>
                      ) : (
                        <Badge variant="warning" icon={<FeatherAlertCircle />}>
                          Not verified
                        </Badge>
                      )}
                      {!settings.email_confirmed && (
                        <Button
                          variant="neutral-tertiary"
                          size="small"
                          disabled={isResending}
                          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            event.preventDefault();
                            handleResendConfirmation();
                          }}
                        >
                          {isResending ? 'Sending...' : 'Resend confirmation email'}
                        </Button>
                      )}
                      <Button
                        variant="destructive-tertiary"
                        size="small"
                        disabled={isRemovingEmail}
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                          event.preventDefault();
                          handleRemoveEmail();
                        }}
                      >
                        Remove email
                      </Button>
                    </div>
                  ) : email && email !== settings?.email ? (
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      disabled={isSettingEmail || !email}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        event.preventDefault();
                        handleSetEmail();
                      }}
                    >
                      {isSettingEmail ? 'Adding...' : 'Add email address'}
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

              {/* Profile Details Section */}
              <div className="flex w-full flex-col items-start gap-6">
                <span className="w-full text-caption font-caption text-subtext-color">
                  PROFILE DETAILS
                </span>
                <TextArea
                  className="h-auto w-full flex-none"
                  label="Bio"
                  helpText={
                    bio.length > 500
                      ? 'Bio must be 500 characters or less'
                      : `Tell us about yourself (${bio.length}/500 characters)`
                  }
                  error={bio.length > 500}
                >
                  <TextArea.Input
                    placeholder="Designer, developer, and creator..."
                    value={bio}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                      setBio(event.target.value);
                      setFormDirty(true);
                    }}
                  />
                </TextArea>
              </div>

              <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

              {/* Social Links Section */}
              <div className="flex w-full flex-col items-start gap-6">
                <span className="w-full text-caption font-caption text-subtext-color">
                  SOCIAL LINKS
                </span>
                <TextField
                  className="h-auto w-full flex-none"
                  label="X (Twitter)"
                  helpText={isActiveAmbassador ? "Your X username cannot be changed while you are an active ambassador. Contact support if you need to update it." : ""}
                  icon={<FeatherTwitter />}
                >
                  <TextField.Input
                    placeholder="username"
                    value={xAccount}
                    disabled={isActiveAmbassador}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setXAccount(event.target.value);
                      setFormDirty(true);
                    }}
                  />
                </TextField>
                <TextField
                  className="h-auto w-full flex-none"
                  label="Website"
                  helpText="Your personal or portfolio website"
                  icon={<FeatherGlobe />}
                >
                  <TextField.Input
                    placeholder="https://yourwebsite.com"
                    type="url"
                    value={website}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setWebsite(event.target.value);
                      setFormDirty(true);
                    }}
                  />
                </TextField>

                {/* Discord Account */}
                <div className="flex w-full flex-col items-start gap-2">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Discord
                  </span>
                  {settings?.social_links?.discord_id ? (
                    <div className="flex w-full items-center gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
                      {settings.social_links.discord_avatar && (
                        <img
                          src={settings.social_links.discord_avatar}
                          alt="Discord avatar"
                          className="h-8 w-8 rounded-full"
                        />
                      )}
                      <span className="grow text-body font-body text-default-font">
                        {settings.social_links.discord_username}
                      </span>
                      <Badge variant="success" icon={<FeatherCheckCircle />}>
                        Linked
                      </Badge>
                      <Button
                        variant="destructive-tertiary"
                        size="small"
                        disabled={isUnlinkingDiscord || isActiveAmbassador}
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                          event.preventDefault();
                          handleUnlinkDiscord();
                        }}
                      >
                        {isUnlinkingDiscord ? 'Unlinking...' : 'Unlink'}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="neutral-secondary"
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        event.preventDefault();
                        window.location.href = getDiscordLinkUrl();
                      }}
                    >
                      Link Discord Account
                    </Button>
                  )}
                  {isActiveAmbassador && settings?.social_links?.discord_id && (
                    <span className="text-caption font-caption text-subtext-color">
                      Your Discord account cannot be unlinked while you are an active ambassador.
                    </span>
                  )}
                </div>
              </div>

              <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

              {/* Reward Addresses Section */}
              <div className="flex w-full flex-col items-start gap-6">
                <div className="flex w-full items-center gap-2">
                  <FeatherGift className="text-subtext-color" />
                  <span className="w-full text-caption font-caption text-subtext-color">
                    REWARD ADDRESSES
                  </span>
                </div>
                <p className="text-body font-body text-subtext-color">
                  Set your payout addresses for project reward claims. These addresses are used when you claim rewards from NFT projects.
                </p>

                {rewardAddressesUpdateError && (
                  <div className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    Failed to update reward addresses: {rewardAddressesUpdateError.message}
                  </div>
                )}

                {isLoadingRewardAddresses ? (
                  <div className="text-subtext-color">Loading reward addresses...</div>
                ) : (
                  <div className="flex w-full flex-col gap-4">
                    <TextField
                      className="h-auto w-full flex-none"
                      label="Coreum Address"
                      helpText={
                        validateCoreum(rewardAddresses.coreum || '') ||
                        'Your Coreum wallet address for receiving reward payouts (starts with core1)'
                      }
                      error={!!validateCoreum(rewardAddresses.coreum || '')}
                    >
                      <TextField.Input
                        placeholder="core1..."
                        value={rewardAddresses.coreum || ''}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          updateRewardAddress('coreum', event.target.value);
                        }}
                      />
                    </TextField>
                  </div>
                )}
              </div>


              <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

              {/* Offer Controls Section */}
              <div className="flex w-full flex-col items-start gap-6">
                <span className="w-full text-caption font-caption text-subtext-color">
                  OFFER CONTROLS
                </span>
                <p className="text-body font-body text-subtext-color">
                  Control who can make offers on all your NFTs. These settings combine with per-NFT controls (the higher minimum wins). On-platform offers are enforced; external offers below your minimum will suppress notifications.
                </p>

                {offerControlsUpdateError && (
                  <div className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    Failed to update offer controls: {offerControlsUpdateError.message}
                  </div>
                )}

                {isLoadingOfferControls ? (
                  <div className="text-subtext-color">Loading offer controls...</div>
                ) : offerControls ? (
                  <div className="flex w-full flex-col gap-4">
                    {/* Disable all offers toggle */}
                    <div className="flex w-full items-start gap-4 py-2">
                      <Switch
                        checked={offerControls.wallet_offers_disabled}
                        onCheckedChange={() => updateOfferControl('wallet_offers_disabled', !offerControls.wallet_offers_disabled)}
                        disabled={isUpdatingOfferControls}
                      />
                      <div className="flex flex-col gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Disable all offers on my NFTs
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          Block all buy offers across your entire wallet
                        </span>
                      </div>
                    </div>

                    {/* Floor-relative minimum section */}
                    {!offerControls.wallet_offers_disabled && (
                      <>
                        <div className="flex w-full items-start gap-4 py-2">
                          <Switch
                            checked={offerControls.wallet_offer_control_mode === 'floor_relative'}
                            onCheckedChange={() =>
                              updateOfferControl(
                                'wallet_offer_control_mode',
                                offerControls.wallet_offer_control_mode === 'floor_relative' ? 'none' : 'floor_relative'
                              )
                            }
                            disabled={isUpdatingOfferControls}
                          />
                          <div className="flex flex-col gap-1">
                            <span className="text-body-bold font-body-bold text-default-font">
                              Set minimum offer relative to collection floor price
                            </span>
                            <span className="text-caption font-caption text-subtext-color">
                              Automatically require offers to be at or above a percentage of the collection floor price
                            </span>
                          </div>
                        </div>

                        {offerControls.wallet_offer_control_mode === 'floor_relative' && (
                          <div className="flex w-full flex-col gap-2 pl-12">
                            <TextField
                              className="h-auto w-full max-w-[300px] flex-none"
                              label="Floor price percentage"
                              helpText={(() => {
                                const pct = offerControls.wallet_offer_floor_percentage;
                                const direction = pct >= 0 ? 'above' : 'below';
                                const absPct = Math.abs(pct);
                                const exampleMin = Math.max(0, Math.round(100 * (1 + pct / 100) * 100) / 100);
                                return `Offers must be at least ${absPct}% ${direction} floor. Example: if floor is 100 XRP, minimum offer = ${exampleMin} XRP`;
                              })()}
                            >
                              <TextField.Input
                                placeholder="e.g., 10"
                                type="number"
                                value={offerControls.wallet_offer_floor_percentage.toString()}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                  const val = parseInt(event.target.value) || 0;
                                  const clamped = Math.max(-99, Math.min(1000, val));
                                  updateOfferControl('wallet_offer_floor_percentage', clamped);
                                }}
                              />
                            </TextField>
                            <span className="text-caption font-caption text-subtext-color">
                              Range: -99% to 1000%. Use negative values to accept offers below floor.
                            </span>
                            <span className="text-caption font-caption text-amber-600">
                              Note: If an NFT's collection has no floor price, no minimum will be enforced.
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {/* Disable trade offers toggle */}
                    <div className="flex w-full items-start gap-4 py-2">
                      <Switch
                        checked={offerControls.wallet_trade_offers_disabled}
                        onCheckedChange={() => updateOfferControl('wallet_trade_offers_disabled', !offerControls.wallet_trade_offers_disabled)}
                        disabled={isUpdatingOfferControls}
                      />
                      <div className="flex flex-col gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Disable incoming trade offers
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          Block all incoming NFT trade proposals from other users
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-subtext-color">Unable to load offer controls</div>
                )}
              </div>

              <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

              {/* Blocked Addresses Section */}
              <div className="flex w-full flex-col items-start gap-6">
                <div className="flex w-full items-center gap-2">
                  <FeatherBan className="text-subtext-color" />
                  <span className="w-full text-caption font-caption text-subtext-color">
                    BLOCKED ADDRESSES
                  </span>
                </div>
                <p className="text-body font-body text-subtext-color">
                  Blocked wallets cannot make offers on your NFTs. Offers from blocked wallets are silently ignored.
                </p>

                {/* Add new block input */}
                <div className="flex w-full items-end gap-3">
                  <TextField
                    className="h-auto grow"
                    label=""
                    helpText=""
                  >
                    <TextField.Input
                      placeholder="Paste an XRPL address to block (r...)"
                      value={blockInput}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setBlockInput(event.target.value);
                      }}
                    />
                  </TextField>
                  <Button
                    variant="destructive-secondary"
                    disabled={!blockInput.trim() || isBlocking}
                    onClick={async (event: React.MouseEvent<HTMLButtonElement>) => {
                      event.preventDefault();
                      const address = blockInput.trim();
                      if (!address) return;
                      try {
                        await blockAddress(address);
                        setBlockInput('');
                        showSuccessToast('Wallet blocked successfully');
                      } catch (err: any) {
                        showErrorToast(err?.message || 'Failed to block wallet');
                      }
                    }}
                  >
                    {isBlocking ? 'Blocking...' : 'Block'}
                  </Button>
                </div>

                {/* Blocked wallets table */}
                {isLoadingBlockedWallets ? (
                  <div className="text-subtext-color">Loading blocked addresses...</div>
                ) : blockedWallets.length === 0 ? (
                  <div className="text-subtext-color text-body font-body">
                    You haven't blocked any wallets yet.
                  </div>
                ) : (
                  <div className="flex w-full flex-col rounded-md border border-solid border-neutral-border overflow-hidden">
                    <div className="flex w-full items-center px-4 py-2 bg-neutral-50">
                      <span className="grow text-caption font-caption text-subtext-color">Address</span>
                      <span className="w-28 flex-none text-caption font-caption text-subtext-color text-center">Blocked on</span>
                      <span className="w-28 flex-none" />
                    </div>
                    {blockedWallets.map((entry) => (
                      <div key={entry.id} className="flex w-full items-center px-4 py-3 border-t border-solid border-neutral-border">
                        <span className="grow text-body font-body text-default-font font-mono text-sm truncate">
                          {entry.blocked_address}
                        </span>
                        <span className="w-28 flex-none text-caption font-caption text-subtext-color text-center">
                          {new Date(entry.created_at).toLocaleDateString()}
                        </span>
                        <div className="w-28 flex-none flex justify-center">
                          {confirmUnblock === entry.blocked_address ? (
                            <Button
                              variant="destructive-primary"
                              size="small"
                              disabled={isUnblocking}
                              onClick={async (event: React.MouseEvent<HTMLButtonElement>) => {
                                event.preventDefault();
                                try {
                                  await unblockAddress(entry.blocked_address);
                                  setConfirmUnblock(null);
                                  showSuccessToast('Wallet unblocked');
                                } catch (err: any) {
                                  showErrorToast(err?.message || 'Failed to unblock wallet');
                                }
                              }}
                            >
                              {isUnblocking ? '...' : 'Confirm?'}
                            </Button>
                          ) : (
                            <Button
                              variant="destructive-secondary"
                              size="small"
                              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                event.preventDefault();
                                setConfirmUnblock(entry.blocked_address);
                              }}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

              {/* Email Notification Settings Section */}
              <div className="flex w-full flex-col items-start gap-6">
                <div className="flex w-full items-center gap-2">
                  <FeatherBell className="text-subtext-color" />
                  <span className="w-full text-caption font-caption text-subtext-color">
                    EMAIL NOTIFICATIONS
                  </span>
                </div>
                <p className="text-body font-body text-subtext-color">
                  Control which email notifications you receive. In-app notifications are always shown regardless of these settings.
                </p>

                {notificationUpdateError && (
                  <div className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    Failed to update notification preferences: {notificationUpdateError.message}
                  </div>
                )}

                {!settings?.email_confirmed && (
                  <div className="w-full bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded">
                    Add and verify your email address above to enable email notifications.
                  </div>
                )}

                {isLoadingPreferences ? (
                  <div className="text-subtext-color">Loading notification preferences...</div>
                ) : notificationPreferences ? (
                  <div className={`flex w-full flex-col gap-4 ${!settings?.email_confirmed ? 'opacity-50' : ''}`}>
                    {/* Offer Received */}
                    <div className="flex w-full items-start gap-4 py-2">
                      <Switch
                        checked={settings?.email_confirmed ? notificationPreferences.notify_on_offer_received : false}
                        onCheckedChange={() => togglePreference('notify_on_offer_received')}
                        disabled={isUpdatingPreferences || !settings?.email_confirmed}
                      />
                      <div className="flex flex-col gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          New offers on my NFTs
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          Get notified when someone makes a buy offer on your NFT
                        </span>
                      </div>
                    </div>

                    {/* NFT Sold */}
                    <div className="flex w-full items-start gap-4 py-2">
                      <Switch
                        checked={settings?.email_confirmed ? notificationPreferences.notify_on_nft_sold : false}
                        onCheckedChange={() => togglePreference('notify_on_nft_sold')}
                        disabled={isUpdatingPreferences || !settings?.email_confirmed}
                      />
                      <div className="flex flex-col gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          NFT sold
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          Get notified when your NFT is sold
                        </span>
                      </div>
                    </div>

                    {/* NFT Bought */}
                    <div className="flex w-full items-start gap-4 py-2">
                      <Switch
                        checked={settings?.email_confirmed ? notificationPreferences.notify_on_nft_bought : false}
                        onCheckedChange={() => togglePreference('notify_on_nft_bought')}
                        disabled={isUpdatingPreferences || !settings?.email_confirmed}
                      />
                      <div className="flex flex-col gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          NFT purchased
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          Get notified when you successfully purchase an NFT
                        </span>
                      </div>
                    </div>

                    {/* Offer Accepted */}
                    <div className="flex w-full items-start gap-4 py-2">
                      <Switch
                        checked={settings?.email_confirmed ? notificationPreferences.notify_on_offer_accepted : false}
                        onCheckedChange={() => togglePreference('notify_on_offer_accepted')}
                        disabled={isUpdatingPreferences || !settings?.email_confirmed}
                      />
                      <div className="flex flex-col gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Offer accepted
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          Get notified when a buy offer you made gets accepted
                        </span>
                      </div>
                    </div>

                    {/* Transfer Offer */}
                    <div className="flex w-full items-start gap-4 py-2">
                      <Switch
                        checked={settings?.email_confirmed ? notificationPreferences.notify_on_transfer_offer : false}
                        onCheckedChange={() => togglePreference('notify_on_transfer_offer')}
                        disabled={isUpdatingPreferences || !settings?.email_confirmed}
                      />
                      <div className="flex flex-col gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Transfer offers
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          Get notified when someone offers to transfer an NFT to you
                        </span>
                      </div>
                    </div>

                    {/* Mint Reminders */}
                    <div className="flex w-full items-start gap-4 py-2">
                      <Switch
                        checked={settings?.email_confirmed ? notificationPreferences.notify_on_mint_reminders : false}
                        onCheckedChange={() => togglePreference('notify_on_mint_reminders')}
                        disabled={isUpdatingPreferences || !settings?.email_confirmed}
                      />
                      <div className="flex flex-col gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Mint reminders
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          Get reminded 24 hours, 1 hour, and when minting goes live for drops you registered for
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-subtext-color">Unable to load notification preferences</div>
                )}
              </div>

              <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

              {/* Action Buttons */}
              <div className="flex w-full items-center justify-between">
                <Button
                  variant="neutral-secondary"
                  disabled={!formDirty && !isNotificationsDirty && !isOfferControlsDirty && !isRewardAddressesDirty}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="large"
                  type="submit"
                  disabled={(isUpdating || isUpdatingPreferences || isUpdatingOfferControls || isUpdatingRewardAddresses) || (!formDirty && !isNotificationsDirty && !isOfferControlsDirty && !isRewardAddressesDirty)}
                >
                  {(isUpdating || isUpdatingPreferences || isUpdatingOfferControls || isUpdatingRewardAddresses) ? 'Saving...' : 'Save changes'}
                </Button>
              </div>
            </form>
      </div>
    </DefaultPageLayout>
  );
};
